import { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { WorkflowPage } from '../../../components';
import JobStatusViewModal from '../../../components/JobStatusViewModal/JobStatusViewModal';
import ContractDetailsModal from '../../../components/ContractDetailsModal/ContractDetailsModal';
import { FORM_CONFIGS } from '../../../constants/formConfigurations';
import { TABLE_COLUMNS, FILTER_CONFIGS, SEARCH_CONFIGS } from '../../../constants/tableConfigurations';
import { useUserRevenueEntries } from '../../../hooks/useUserRevenueEntries';
import { useAuth, useFirms, useBonusRevenueTypes } from '../../../hooks';
import { useRevenueCalculationRules } from '../../../hooks/useRevenueCalculationRules';
import { enrichRevenueEntries, buildRuleLookupKey } from '../../../utils/revenueEntryEnrichment';
import { buildRevenueFormFields } from '../../../utils/revenueFormFields';
import CiroTalepDetailModal from './CiroTalepDetailModal';
import CiroRaporModal from './CiroRaporModal';

// ROOT-only Ciro Talepleri sayfası — onayci ekrani.
// useUserRevenueEntries({ userId: null, paginated: false }) → ROOT için tüm
// şirket kayıtları (hook içindeki where, ROOT + userId yoksa {} döner).
// Route'ta PermissionRoute guard'i yok; ROOT olmayan bir kullanici dogrudan
// URL ile gelirse asagidaki explicit fallback boş ekran gosterir.
const CiroTalepleriPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const {
    userRevenueEntries,
    isLoading,
    handleFormSubmit,
    handleApprove,
    handleReject,
  } = useUserRevenueEntries({ userId: null, paginated: false });

  // Düzenle akışında dropdown'larda firma/sözleşme/iş gösterebilmek için
  // useFirms (nested contracts + jobs döndürür) ve bonus_revenue_types
  // hook'lari. CiroEklePage ile aynı pattern.
  const { firms, fetchData: fetchFirms } = useFirms();
  const { bonusRevenueTypes, fetchData: fetchBonusRevenueTypes } = useBonusRevenueTypes();
  // ONAY_SONRASI x/y kolonu hesabi icin — rule set'ini fetch et.
  // useDynamicEntity auto-fetch yapiyor; ROOT'ta company_id filtresi yok.
  const { revenueCalculationRules: revenueRules } = useRevenueCalculationRules();

  useEffect(() => {
    if (user?.id) {
      fetchFirms();
      fetchBonusRevenueTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // bonus_revenue_type_id → coefficient + name lookup'lari. Ana data'da
  // bonus_revenue_type relationship olmadigi icin ayri hook'tan cozuyoruz.
  const coefficientById = useMemo(() => {
    const map = new Map();
    (bonusRevenueTypes || []).forEach((b) => {
      const c = Number(b?.coefficient);
      map.set(b.id, Number.isFinite(c) ? c : 1);
    });
    return map;
  }, [bonusRevenueTypes]);

  const typeNameById = useMemo(() => {
    const map = new Map();
    (bonusRevenueTypes || []).forEach((b) => {
      map.set(b.id, b.name || `#${b.id}`);
    });
    return map;
  }, [bonusRevenueTypes]);

  // Rule lookup: (job_type_id | revenue_channel | pricing_method_id) → rule.
  // Bir rule pricing_method_ids array'i tasidigi icin her id icin ayri key uret.
  const rulesByKey = useMemo(() => {
    const map = new Map();
    (revenueRules || []).forEach((rule) => {
      if (!rule || rule.status === 'inactive') return;
      const ids = Array.isArray(rule.pricing_method_ids) ? rule.pricing_method_ids : [];
      const jobTypeId = rule.job_type_id ?? null;
      const channel = rule.revenue_channel;
      if (ids.length === 0) {
        map.set(buildRuleLookupKey(jobTypeId, channel, null), rule);
      } else {
        ids.forEach((pmId) => {
          map.set(buildRuleLookupKey(jobTypeId, channel, pmId), rule);
        });
      }
    });
    return map;
  }, [revenueRules]);

  // Tablo render'larinin runtime-context'siz tuketebilmesi icin satirlara
  // targetRevenue + warningsKeys + warningsOnlyUygun + bonusRevenueTypeName + xyLabel ekliyoruz.
  const enrichedData = useMemo(() => {
    const zengin = enrichRevenueEntries(userRevenueEntries, {
        coefficientById,
        typeNameById,
        // Onaylayan ROOT'un team_id'si — onayci tipik olarak ROOT
        // (team_id null) oldugundan farkli ekip uyarisini sustur.
        currentUserTeamId: user?.team_id ?? null,
        rulesByKey,
      });
    // Ibrahim: "beklemede olanlar hep en ustte gorunsun." Array.sort kararli;
    // her grubun kendi ici (yil/ceyrek/olusturma sirasi) aynen korunur.
    const beklemeOnce = (r) => (r?.status === 'PENDING' ? 0 : 1);
    return [...zengin].sort((a, b) => beklemeOnce(a) - beklemeOnce(b));
  }, [userRevenueEntries, coefficientById, typeNameById, user?.team_id, rulesByKey]);

  // CiroRaporModal icin ekip listesi — entry.user.team_id -> team.name.
  const teams = useMemo(() => {
    const teamMap = new Map();
    (userRevenueEntries || []).forEach((entry) => {
      const teamId = entry?.user?.team_id;
      const teamName = entry?.user?.team?.name;
      if (teamId != null && teamName) teamMap.set(teamId, teamName);
    });
    return Array.from(teamMap.entries()).map(([id, name]) => ({ id, name }));
  }, [userRevenueEntries]);

  // Filtre config'i tamamen FILTER_CONFIGS'tan geliyor — options DataTable
  // tarafindan tablo verisinden auto-generate ediliyor (diger dinamik filtrelerle
  // ayni pattern: kanal/donem/firma). Statik options injection kaldirildi.
  const filterConfigs = FILTER_CONFIGS.userRevenueEntries || [];

  // Düzenleme formu — firms + bonusRevenueTypes options injection. ROOT
  // akisinda "Tutar Getir" gibi otomatik hesap ek alanlar yok; onayci
  // tutar/ay_sayisi'ni elle duzeltebilir.
  const formFields = useMemo(
    () => buildRevenueFormFields({ firms, bonusRevenueTypes }),
    [firms, bonusRevenueTypes],
  );

  const statusCounts = useMemo(() => {
    const counts = { all: 0, PENDING: 0, APPROVED: 0, REJECTED: 0 };
    if (!Array.isArray(userRevenueEntries)) return counts;
    counts.all = userRevenueEntries.length;
    userRevenueEntries.forEach((row) => {
      if (counts.hasOwnProperty(row.status)) counts[row.status] += 1;
    });
    return counts;
  }, [userRevenueEntries]);

  // Detay modal — PENDING dışındaki kayıtlarda "Görüntüle" ile açılır.
  const [detailEntry, setDetailEntry] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Rapor modal — header'daki "Rapor" butonu acar.
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // İş durumu ve sözleşme görüntüleme modal state'leri.
  const [jobStatusModalOpen, setJobStatusModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Edit modal'a girerken row'u form alanlarina map et.
  const transformEditData = useCallback((row) => {
    if (!row) return {};
    return {
      yil: row.yil,
      ceyrek: row.ceyrek,
      ciro_kanali: row.ciro_kanali,
      bonus_revenue_type_id: row.bonus_revenue_type_id,
      firm_id: row.firm_id,
      contract_id: row.contract_id ?? '',
      job_id: row.job_id ?? '',
      tutar: row.tutar,
      ay_sayisi: row.ay_sayisi ?? '',
      // Payda (y) — DB'de toplam_ay kolonu. Formdaki "Prim Odemesine
      // Iliskin Sure — Payda (y)" alani bu deger ile acilir. Onceden
      // formData'ya set edilmiyordu, edit acilirken payda kutusu bos
      // goruluyordu (bug: CiroTalepleri sayfasi).
      toplam_ay: row.toplam_ay ?? '',
      // ONAY_SONRASI_CIROSU multiselect'in edit modalinda dolu gelmesi icin.
      // Eski kayitlarda NULL geliyor — bos array'e normalize et.
      secili_aylar: Array.isArray(row.secili_aylar) ? row.secili_aylar : [],
      aciklama: row.aciklama ?? '',
      user_id: row.user_id,
    };
  }, []);

  const rowActions = useCallback(
    (row) => {
      const actions = [
        // Tüm satırlarda "Görüntüle" (detay modal).
        {
          type: 'review',
          label: 'userRevenueEntries.actions.review',
          color: 'info',
          icon: <SearchIcon fontSize="small" />,
        },
        {
          type: 'edit',
          label: 'userRevenueEntries.actions.edit',
          color: 'primary',
          icon: <EditIcon fontSize="small" />,
        },
      ];
      if (row?.job) {
        actions.push({
          type: 'viewJobStatus',
          label: 'userRevenueEntries.actions.viewJobStatus',
          color: 'info',
          icon: <AssignmentIcon fontSize="small" />,
        });
      }
      if (row?.contract || row?.job?.contract) {
        actions.push({
          type: 'viewContract',
          label: 'userRevenueEntries.actions.viewContract',
          color: 'primary',
          icon: <DescriptionIcon fontSize="small" />,
        });
      }
      if (user?.role === 'ROOT' && row.status === 'PENDING') {
        actions.push(
          {
            type: 'approve',
            label: 'userRevenueEntries.actions.approve',
            color: 'success',
            icon: <CheckCircleIcon fontSize="small" />,
          },
          {
            type: 'reject',
            label: 'userRevenueEntries.actions.reject',
            color: 'error',
            icon: <CancelIcon fontSize="small" />,
          },
        );
      }
      // ROOT her statude kaydi silebilir (onaylanmis primler dahil).
      // Onceden yalniz PENDING'de approve/reject vardi ama delete hic
      // eklenmiyordu — kullanici APPROVED bir primi silmek istedigi anda
      // aksiyon menusunde secenek bulamiyordu.
      if (user?.role === 'ROOT') {
        actions.push({
          type: 'delete',
          label: 'table.actions.delete',
          color: 'error',
        });
      }
      return actions;
    },
    [user?.role],
  );

  // WorkflowPage 'approve'/'reject' aksiyonlarını kendi içinde modal'a
  // bağlıyor; 'review' bizim custom aksiyonumuz, onu page seviyesinde
  // yakalayıp detay modal'ı açıyoruz. 'edit' aksiyonu DataTableContainer'in
  // default davranisina birakiyoruz (return false) — built-in edit modal
  // formFields ile acilir.
  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'review' && row) {
      setDetailEntry(row);
      setDetailModalOpen(true);
      return true;
    }
    if (actionType === 'viewJobStatus' && row?.job) {
      setSelectedJob(row.job);
      setJobStatusModalOpen(true);
      return true;
    }
    if (actionType === 'viewContract' && (row?.contract || row?.job?.contract)) {
      setSelectedContract(row.contract || row.job.contract);
      setContractModalOpen(true);
      return true;
    }
    return false;
  }, []);

  // Edit submit'i useUserRevenueEntries.handleFormSubmit'e yonlendir.
  const handleEditSubmit = useCallback(
    async (formData, isEditMode, currentEditData) => {
      const entityId = isEditMode ? currentEditData?.id ?? null : null;
      return handleFormSubmit(formData, entityId);
    },
    [handleFormSubmit],
  );

  const handleCloseDetail = useCallback(() => {
    setDetailModalOpen(false);
    setDetailEntry(null);
  }, []);

  const handleOpenReport = useCallback(() => setReportModalOpen(true), []);
  const handleCloseReport = useCallback(() => setReportModalOpen(false), []);

  const handleCloseJobStatusModal = useCallback(() => {
    setJobStatusModalOpen(false);
    setSelectedJob(null);
  }, []);

  const handleCloseContractModal = useCallback(() => {
    setContractModalOpen(false);
    setSelectedContract(null);
  }, []);

  // ROOT degilse erken don. useUserRevenueEntries hook'u zaten role-check
  // yapip bos liste donduruyor; bu fallback URL'i bilenlerin UI'a
  // erisememesi icin explicit savunma katmani.
  if (user && user.role !== 'ROOT') return null;

  return (
    <>
      <WorkflowPage
        entityType="userRevenueEntries"
        data={enrichedData}
        loading={isLoading}
        columns={TABLE_COLUMNS.userRevenueEntries || []}
        formFields={formFields}
        filterConfigs={filterConfigs}
        searchConfig={SEARCH_CONFIGS.userRevenueEntries || {}}
        formConfig={{
          fields: formFields,
          title: FORM_CONFIGS.userRevenueEntries?.title || {},
        }}
        statusCounts={statusCounts}
        onApprove={handleApprove}
        onReject={handleReject}
        onFormSubmit={handleEditSubmit}
        transformEditData={transformEditData}
        rowActions={rowActions}
        onRowAction={handleRowAction}
        canAdd={false}
        topPagination
        currentPage={t('pages.ciroTalepleri')}
        showViewGuard={false}
        extraHeaderActions={[
          {
            key: 'report',
            label: 'userRevenueEntries.report.title',
            icon: <DownloadIcon fontSize="small" />,
            onClick: handleOpenReport,
            color: 'primary',
            variant: 'outlined',
          },
        ]}
      />
      <CiroTalepDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetail}
        entry={detailEntry}
        coefficient={
          detailEntry
            ? coefficientById.get(detailEntry.bonus_revenue_type_id) ?? 1
            : 1
        }
      />
      <CiroRaporModal
        open={reportModalOpen}
        onClose={handleCloseReport}
        entries={userRevenueEntries}
        teams={teams}
        bonusRevenueTypes={bonusRevenueTypes}
        coefficientById={coefficientById}
      />
      <JobStatusViewModal
        open={jobStatusModalOpen}
        onClose={handleCloseJobStatusModal}
        job={selectedJob}
      />
      <ContractDetailsModal
        open={contractModalOpen}
        onClose={handleCloseContractModal}
        contract={selectedContract}
      />
    </>
  );
};

export default CiroTalepleriPage;
